'use client';

import { useState } from 'react';
import Link from '@/components/Link';
import { Drawer } from 'ui/components/Drawer/drawer';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'ui/components/AccordionV3/Accordion';
import { Button } from 'ui/components/ButtonV2/Button';
import { StaticIcon } from 'ui/components/Icon/Icon';
import { cltw } from 'ui/utils';
import { type NavigationTree } from '@/components/Navigation/getShopNavigation';
import { NavigationMenuListItem } from '@/components/NavigationMenuListItem/NavigationMenuListItem';
import { Menu } from 'ui/icons/icons/Navigation/Menu';

interface SidebarProps {
  navigationTree: NavigationTree[];
  ctaLabel: string;
}

export function Sidebar({
  navigationTree,
  ctaLabel,
}: SidebarProps): JSX.Element {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const buttonClassName =
    'w-auto flex gap-2 px-3 xl:px-5 text-white hover:bg-black whitespace-nowrap';

  return (
    <>
      <Button
        aria-label='menu'
        className={cltw(buttonClassName, 'flex xl:hidden')}
        data-drawer-trigger='sidebar'
        onClick={() => setIsSidebarOpen((isOpen) => !isOpen)}
        size='medium'
        variant='transparent'
      >
        <StaticIcon
          IconComponent={Menu}
          alt='menu'
          color='#ffffff'
          size='small'
        />
      </Button>
      <Drawer
        className='bg-black text-white sm:w-[400px]'
        drawerId='sidebar'
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
      >
        <div className='mx-4'>
          <Accordion collapsible type='single'>
            {navigationTree.map((root) => (
              <AccordionItem key={root.label} value={root.label}>
                {root.link && root.children?.length === 0 ? (
                  // Navigation item with no children, should not display accordion button and should be a link
                  // TODO: Add a test for this
                  <Link
                    className='block py-2 text-white hover:underline'
                    href={root.link}
                  >
                    {root.label}
                  </Link>
                ) : (
                  <AccordionTrigger className='text-white transition-all [&>svg]:w-[24px] [&>svg]:fill-white [&>svg]:transition-all'>
                    <div className='me-2 flex items-center'>{root.label}</div>
                  </AccordionTrigger>
                )}
                {root.children ? (
                  <AccordionContent className='mt-0'>
                    <ul className='flex flex-col px-3 pt-1'>
                      {root.children.map((node) => (
                        <NavigationMenuListItem
                          className='ps-0 [&>span]:font-bold [&>span]:uppercase'
                          href={node.link}
                          key={node.label}
                          title={node.label}
                        >
                          <ul>
                            {node.children?.map((leaf) => (
                              <NavigationMenuListItem
                                className=''
                                href={leaf.link}
                                key={leaf.link}
                                title={leaf.label}
                              />
                            ))}
                          </ul>
                        </NavigationMenuListItem>
                      ))}
                    </ul>
                  </AccordionContent>
                ) : null}
              </AccordionItem>
            ))}
          </Accordion>
          <Button
            aria-label='join whoop'
            className='mt-4 flex w-auto items-center justify-center whitespace-nowrap px-5'
            size='small'
          >
            <a
              href='https://join.whoop.com'
              rel='noopener noreferrer'
              target='_blank'
            >
              {ctaLabel}
            </a>
          </Button>
        </div>
      </Drawer>
    </>
  );
}
